import { useEffect, useLayoutEffect, useRef, useState } from "react";

export type AnimationPhase = "idle" | "offset" | "animating" | "exitOffset" | "exitAnimating";

export const ANIMATED_BANNER_DURATION_MS = 500;

interface UseAnimatedBannerWidthOptions {
  isVisible: boolean;
  /** When false, skip enter/exit animations (e.g. banner should not show at all). */
  enabled?: boolean;
}

export const useAnimatedBannerWidth = ({
  isVisible,
  enabled = true,
}: UseAnimatedBannerWidthOptions) => {
  const prevIsVisible = useRef(isVisible);
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("idle");
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bannerContentRef = useRef<HTMLDivElement | null>(null);
  const [bannerWidth, setBannerWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const wasVisible = prevIsVisible.current;
    const isEntering = !wasVisible && isVisible;
    const isExiting = wasVisible && !isVisible;

    if (isEntering) {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

      if (bannerContentRef.current) {
        setBannerWidth(bannerContentRef.current.scrollWidth);
      }

      setAnimationPhase("offset");

      requestAnimationFrame(() => {
        setAnimationPhase("animating");
      });

      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase("idle");
        animationTimeoutRef.current = null;
      }, ANIMATED_BANNER_DURATION_MS);
    } else if (isExiting) {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

      if (bannerContentRef.current) {
        setBannerWidth(bannerContentRef.current.scrollWidth);
      }

      setAnimationPhase("exitOffset");

      requestAnimationFrame(() => {
        setAnimationPhase("exitAnimating");
      });

      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase("idle");
        animationTimeoutRef.current = null;
      }, ANIMATED_BANNER_DURATION_MS);
    }

    if (isVisible && animationPhase !== "idle" && !animationTimeoutRef.current) {
      setAnimationPhase("idle");
    }

    prevIsVisible.current = isVisible;
  }, [isVisible, enabled, animationPhase]);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  return {
    animationPhase,
    bannerContentRef,
    bannerWidth,
  };
};
